// @flow
// TODO (CUI-3594): Deduplicate this file with app/packages/validators/validator
// The above package was a copy/paste job created for mWeb but there was never any cleanup to consolidate the two.
import * as StringUtil from 'app/common/lib/StringUtil';
import i18n from 'app/common/lib/i18n';
import testStrength from 'app/packages/validators/passwordStrength';
import urlValidationRegex from 'app/common/lib/react/urlValidationRegex';
import {
  validatePasswordUserMatch,
  validatePasswordList,
} from 'app/common/lib/react/newPasswordStrength';

type ErrorsAndWarnings = {| errors: Array<string>, warnings: Array<string> |};
type ErrorsWarningsScore = {|
  ...ErrorsAndWarnings,
  score: number,
|};
type Message = {| message: string |};
type ValidatorCallback = (data: ErrorsAndWarnings) => void;

export type Validator = (
  value: string,
  callback?: (data: ErrorsAndWarnings | ErrorsWarningsScore) => void,
) => ErrorsAndWarnings | ErrorsWarningsScore;

const WEAK_PASSWORDS = [
  'pinterest',
  'password',
  '123456',
  'welcome',
  'ninja',
  'abc123',
  '123456789',
  '12345678',
  'sunshine',
  'princess',
  'qwerty',
  'master',
  'michael',
  'jordan',
  'dragon',
  'soccer',
  'killer',
  'pepper',
  'devil',
  'princess',
  '1234567',
  'iloveyou',
];

// This regular expression is the same as the regex in config/emails.py
export const EMAIL_REGEX = new RegExp(
  [
    "(^[-!#$%&'*+/=?^_`{}|~0-9A-Z]+(\\.[-!#$%&'*+/=?^_`{}|~0-9A-Z]+)*",
    '|^"([\\001-\\010\\013\\014\\016-\\037!#-\\[\\]-\\177]|\\\\[\\001-\\011\\013\\014\\016-\\177])*"',
    ')@((?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\\.)+[A-Z]{2,20}$)',
    '|\\[(25[0-5]|2[0-4]\\d|[0-1]?\\d?\\d)(\\.(25[0-5]|2[0-4]\\d|[0-1]?\\d?\\d)){3}\\]$',
  ].join(''),
  'i',
);

/**
 * Converts fullWwdth characters to halfwidth characters.
 * BUG-3221
 */
const parseFullwidthValues = (n) => {
  const replaceMap = {
    '０': '0',
    '１': '1',
    '２': '2',
    '３': '3',
    '４': '4',
    '５': '5',
    '６': '6',
    '７': '7',
    '８': '8',
    '９': '9',
    '　': ' ',
  };
  n = String(n).split('');

  return n.map((val) => replaceMap[val] || val).join('');
};

/**
 * Returns a validation function that checks:
 * - If element is a checkbox, is it checked?
 * - If element is a set of radio buttons, is one checked?
 * - Does element have a non-false value?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function Required(options?: Message) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];
    if (!value || !/\S/.test(value)) {
      // TODO requires design - tweak
      const message = options && options.message ? options.message : i18n._('Required');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid email address?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function Email(options?: Message) {
  return function(value: string, callback?: ValidatorCallback): ErrorsAndWarnings {
    // This regular expression is the same as the regex in config/emails.py
    const emailRegex = new RegExp(
      [
        "(^[-!#$%&'*+/=?^_`{}|~0-9A-Z]+(\\.[-!#$%&'*+/=?^_`{}|~0-9A-Z]+)*",
        '|^"([\\001-\\010\\013\\014\\016-\\037!#-\\[\\]-\\177]|\\\\[\\001-\\011\\013\\014\\016-\\177])*"',
        ')@((?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\\.)+[A-Z]{2,20}$)',
        '|\\[(25[0-5]|2[0-4]\\d|[0-1]?\\d?\\d)(\\.(25[0-5]|2[0-4]\\d|[0-1]?\\d?\\d)){3}\\]$',
      ].join(''),
      'i',
    );

    value = value && value.trim();
    const errors = [];
    if (value && !value.match(emailRegex)) {
      // TODO requires design - tweak
      const message = options && options.message ? options.message : i18n._('Not a valid email.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid name?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */
export function Name(options: Message) {
  return function(value: ?string, callback: ValidatorCallback): ErrorsAndWarnings {
    value = value && value.trim();
    const errors = [];

    // don't want blanks, don't want it to start with a number
    if (value && !value.match(/^[^0-9].*$/)) {
      const message = options && options.message ? options.message : i18n._('Not a valid name.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }

    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid age?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */
export function Age(options: Message) {
  return function(value: string, callback: ValidatorCallback): ErrorsAndWarnings {
    let age = value.trim();
    age = parseFullwidthValues(age);

    const errors = [];
    const maxAge = 130; // increase this when we have more centenarians
    let message = options && options.message ? options.message : i18n._('Not a valid age.');
    if (age && age !== '' && age.length < 2) {
      message = i18n._('Please enter at least two digits.');
      errors.push(message);
    }
    const parsedAge = Math.floor(Number(age));
    // we don't want to block users from signing up if they don't want to enter age
    if (age !== '' && (!/^\d+$/.test(age) || !(parsedAge > 0 && parsedAge < maxAge))) {
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid phone number?
 *
 * Note: set options.checkLength=false to skip check on length.
 *
 * @param {{
 *        message: (string|undefined)
 *        checkLength: (bool|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function PhoneNumber(options?: {| message?: string, checkLength?: boolean |}) {
  return function(value: string, callback?: ValidatorCallback): ErrorsAndWarnings {
    value = value && value.trim();
    const rawNumbers = value.replace(/[\(\)\+\-\. ]/g, '');
    const phoneNumberRegex = /^(\(?\+?[0-9]*\)?)?[0-9_\- \.\(\)]*$/g;
    const errors = [];
    const checkLength = options && options.checkLength;
    const lengthCondition = checkLength !== false && rawNumbers.length < 7;

    if (value && (!value.match(phoneNumberRegex) || lengthCondition)) {
      const message = options && options.message ? options.message : i18n._('Not a phone number.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid number?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function IsNumber(options: Message) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const numberRegex = /^\d+$/;
    const errors = [];

    if (typeof value === 'number') {
      // Do nothing, this is good
    } else if (typeof value === 'string') {
      value = value && value.trim();
      value = parseFullwidthValues(value);

      if (value && !value.match(numberRegex)) {
        const message = options && options.message ? options.message : i18n._('Not a number.');
        errors.push(message);
      }
    }

    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid verification code for two-factor auth?
 *
 * The code is a number, with optional spaces or '-' (to simplify copy & paste)
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */
export function MfaCode(options: Message) {
  return function(value: ?(string | number), callback: ValidatorCallback): ErrorsAndWarnings {
    const numberRegex = /^[\d\ -]+$/;
    const errors = [];

    // If typeof value === 'number' do nothing, this is good
    if (typeof value === 'string') {
      value = value && value.trim();
      value = parseFullwidthValues(value);

      if (value && !value.match(numberRegex)) {
        const message =
          options && options.message ? options.message : i18n._('Not a verification code.');
        errors.push(message);
      }
    }

    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid URL?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// packages/validators/validator.js version uses a different regex
// looks like this one has been updated for bugs
// Verify which one to use before replacing these usages
export function URL(options?: Message) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];
    if (value && !value.match(urlValidationRegex)) {
      const message = options && options.message ? options.message : i18n._('Not a valid url.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid Hex color
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */
export function HexColor(options?: Message) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];
    if (value && !value.match(/(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i)) {
      const message =
        options && options.message ? options.message : i18n._('Not a valid hex color');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Validates password length meets requirements
 *
 * @param {string} password
 * @param {number|undefined} minLength
 * @return {boolean}
 */
function validatePasswordLength(password: string, minLength: ?number): boolean {
  if (!minLength) {
    minLength = 6;
  }
  return !!password && password.length >= minLength;
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a strong enough password?
 *
 * Provides stronger checks than old Password validator
 *
 * @param {{
 *        blackPassStr: (string|undefined)
 *        samePasswordStr: (string|undefined)
 *        shortPassStr: (string|undefined)
 *        userid: (string|undefined)
 *        }=} options
 * @return {Function} Validation function
 */
type NewPasswordValidator = {|
  blackPassStr?: string,
  samePasswordStr?: string,
  shortPassStr?: string,
  username?: string,
|};

export function NewPassword(options?: NewPasswordValidator) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];

    if (!value) {
      errors.push(i18n._('Required'));
    } else if (!validatePasswordLength(value)) {
      errors.push((options && options.shortPassStr) || i18n._('Too short'));
    } else if (
      !validatePasswordUserMatch(value, options && options.username ? options.username : '')
    ) {
      errors.push(
        (options && options.samePasswordStr) || i18n._("Password and username can't match"),
      );
    } else if (!validatePasswordList(value)) {
      errors.push(
        (options && options.blackPassStr) || i18n._('Please try using a stronger password'),
      );
    }

    const data = {
      errors,
      warnings: [],
    };

    if (callback) {
      callback(data);
    }

    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a strong enough password?
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */
type PasswordValidator = {|
  badPassStyle?: string,
  blackPassStr?: string,
  goodPassStr?: string,
  samePasswordStr?: string,
  shortPassStr?: string,
  username?: string,
|};

type PasswordValidatorCallback = (data: ErrorsWarningsScore) => void;

export function Password(options: PasswordValidator) {
  return function(value: ?string, callback: PasswordValidatorCallback): ErrorsWarningsScore {
    const errors = [];

    const testStrengthOptions = {
      shortPassStr: (options && options.shortPassStr) || i18n._('Too short'),
      badPassStyle: (options && options.badPassStyle) || i18n._('Not proper style'),
      samePasswordStr:
        (options && options.samePasswordStr) || i18n._("Password and username can't match"),
      blackPassStr:
        (options && options.blackPassStr) || i18n._('Please try using a stronger password'),
      goodPassStr: (options && options.goodPassStr) || i18n._('Great!'),
      userid: options && options.username && options.username.length ? options.username : '',
    };

    const strengthResult = testStrength(value, testStrengthOptions, WEAK_PASSWORDS);

    const { fieldInfo, score } = strengthResult;
    const hasError = fieldInfo[fieldInfo.length - 1];

    const message = fieldInfo[0];
    if (hasError && message) {
      errors.push(message);
    }

    const data = {
      errors,
      warnings: [],
      score,
    };

    if (callback) {
      callback(data);
    }

    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Does the value of element have a length >= options['min'] (if defined)
 *
 * @param {{
 *        messages: (!Object|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

type LengthValidator = {|
  max?: number,
  messages?: {| min?: string, max?: string |},
  min?: number,
  should_trim?: boolean,
|};

// Basic logic is the same as packages/validators/validator.js
// But the package version is stricter in what it checks
export function Length(options: LengthValidator) {
  return function(value: string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const max = options.max || Infinity;
    const min = options.min || 0;
    const shouldTrim = options.should_trim || false;
    let defaultMessage;
    const errors = [];
    let message;

    if (shouldTrim) {
      value = value && value.trim();
    }

    const numChars = value.length;

    if (numChars < min) {
      defaultMessage = StringUtil.dangerouslyFormat(
        i18n._('Please enter at least {{ character_count }} characters.'),
        { character_count: min.toString() },
      );
      message = options.messages && options.messages.min ? options.messages.min : defaultMessage;
      errors.push(message);
    } else if (numChars > max) {
      defaultMessage = StringUtil.dangerouslyFormat(
        i18n._('Please enter no more than {{ character_count }} characters.'),
        { character_count: max.toString() },
      );
      message =
        options && options.messages && options.messages.max ? options.messages.max : defaultMessage;
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - If the value of element matches the value of options['targetValue']
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

type EqualToValidator = {|
  message: string,
  targetValue: string,
|};

// Confirmed the same as packages/validators/validator.js
export function EqualTo(options: EqualToValidator) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const { targetValue } = options;
    const errors = [];

    if (value !== targetValue) {
      errors.push(options.message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - If the value of element is greater than options['value']
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

type GreaterThanValidator = {|
  message: string,
  value: number,
|};

export function GreaterThan(options: GreaterThanValidator) {
  return function(value: string | number, callback: ValidatorCallback): ErrorsAndWarnings {
    const testValue = parseFloat(value);
    const errors = [];

    if (testValue <= options.value) {
      errors.push(options.message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - If the value of element is greater than or equal to options['value']
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function GreaterThanOrEqualTo(options: GreaterThanValidator) {
  return function(value: string | number, callback?: ValidatorCallback): ErrorsAndWarnings {
    const testValue = parseFloat(value);
    const errors = [];

    if (testValue < options.value) {
      errors.push(options.message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - If date specified in element is later than or equal to
 *   options['date'] (which should be a Date object)
 *
 * Note it only validates when the field is a date and is enabled.
 *
 * @param {{
 *        message: (string|undefined),
 *        date: (Date)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
// with exception of check !value
export function DateString(options: {| message: string |}) {
  return function(value: ?string, callback: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];

    if (!value || isNaN(Date.parse(value))) {
      errors.push(options.message);
    }

    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - If date specified in element is
 *    - later than or equal to options['date'] (which should be a Date object)
 *      if flip flag is falsy (default)
 *    - earlier than or equal to options['date'] (which should be a Date object)
 *      if flip flag is true
 *
 * Note it only validates when the field is a date and is enabled.
 *
 * @param {{
 *        message: (string|undefined),
 *        date: (Date),
 *        flip: (boolean|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

type DateOrderValidator = {|
  date: Date,
  flip?: boolean,
  message: string,
|};

export function DateOrder(options: DateOrderValidator) {
  return function(value: string, callback: ValidatorCallback): ErrorsAndWarnings {
    const latestDate = options.flip ? options.date : new Date(value);
    const earliestDate = options.flip ? new Date(value) : options.date;
    const errors = [];

    if (latestDate instanceof Date && earliestDate instanceof Date && latestDate < earliestDate) {
      errors.push(options.message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - If the value of element contains only ASCII characters
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */
export function ContainsOnlyAscii(options: Message) {
  return function(value: string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];

    const is_only_ascii = /^[\040-\176]*$/.test(value);
    if (!is_only_ascii) {
      errors.push(options.message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a valid currency value.
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @param {boolean|undefined} hasFractionalCents
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function CurrencyValue(options: Message, hasFractionalCents: boolean = false) {
  return function(value: ?string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const numberRegex = hasFractionalCents ? /^\d*(\.\d\d?\d?)?$/ : /^\d*(\.\d\d?)?$/;
    const errors = [];

    if (typeof value === 'number') {
      value = value.toString();
    }

    if (value && !value.match(numberRegex)) {
      value = value && value.trim();
      const message =
        options && options.message ? options.message : i18n._('Not a valid currency value.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a date.
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function isDate(options?: Message) {
  return function(value: string, callback?: ValidatorCallback): ErrorsAndWarnings {
    value = value && value.trim();
    const errors = [];
    const date = new Date(value);

    if (value && !date.getTime()) {
      const message = options && options.message ? options.message : i18n._('Not a valid date.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks:
 * - Is the value of element a time (e.g. 1:23, 01:23, 13:23, 23:59, 00:00).
 *
 * @param {{
 *        message: (string|undefined)
 *        }=} options Custom error message.
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
export function Time(options?: Message) {
  return function(value: ?string, callback: ValidatorCallback): ErrorsAndWarnings {
    value = value && value.trim();
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    const errors = [];

    if (value && !value.match(timeRegex)) {
      const message = options && options.message ? options.message : i18n._('Not a valid time.');
      errors.push(message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

/**
 * Returns a validation function that checks
 * - Whether the value of element matches the provided regex options['pattern']
 * @param {{
 *        pattern: RegExp,
 *        message: string
 *        }=} options
 * @return {Function} Validation function
 */

// Confirmed the same as packages/validators/validator.js
// except that options is required in the package
export function MatchesRegex(options: ?{ message: string, pattern: RegExp }) {
  return function(value: string, callback?: ValidatorCallback): ErrorsAndWarnings {
    const errors = [];

    if (options && !options.pattern.test(value)) {
      errors.push(options.message);
    }
    const data = {
      errors,
      warnings: [],
    };
    if (callback) {
      callback(data);
    }
    return data;
  };
}

// $FlowFixMe Flow v0.104.0 upgrade supression.
export function getErrors(validators: Array<Validator>, value: *): Array<string> {
  const errors = validators.map((validator) => validator(value).errors);

  return [].concat(...errors);
}
